import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, FileText, Users, Search, Plus, ChevronDown, Clock, CheckCircle, AlertTriangle, Archive } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ConsilierDosarModal from "@/components/ConsilierDosarModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  nou: "bg-blue-100 text-blue-700",
  in_verificare: "bg-yellow-100 text-yellow-700",
  acceptat: "bg-emerald-100 text-emerald-700",
  in_lucru: "bg-purple-100 text-purple-700",
  finalizat: "bg-green-100 text-green-700",
  respins: "bg-red-100 text-red-700"
};

const STATUS_LABELS = { nou: "Nou", in_verificare: "In verificare", acceptat: "Acceptat", in_lucru: "In lucru", finalizat: "Finalizat", respins: "Respins" };
const VALID_STATUSES = ["nou", "in_verificare", "acceptat", "in_lucru", "finalizat", "respins"];

const ConsilierDashboard = () => {
  const navigate = useNavigate();
  const { adminToken, logoutAdmin } = useAuth();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [dosare, setDosare] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dosarFilter, setDosarFilter] = useState("active");
  const [selectedDosar, setSelectedDosar] = useState(null);
  const [statusModal, setStatusModal] = useState(null);
  const [sumaFinalizare, setSumaFinalizare] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${adminToken}` }), [adminToken]);

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, dosareRes] = await Promise.all([
        axios.get(`${API}/consilier/leads`, { headers }),
        axios.get(`${API}/consilier/dosare`, { headers })
      ]);
      setLeads(leadsRes.data);
      setDosare(dosareRes.data);
    } catch { toast.error("Eroare la incarcarea datelor"); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => {
    if (!adminToken) { navigate("/affiliate/login"); return; }
    fetchData();
  }, [adminToken, navigate, fetchData]);

  const handlePreluare = async (leadId) => {
    try {
      const res = await axios.post(`${API}/consilier/leads/${leadId}/preluare`, {}, { headers });
      toast.success(`Lead preluat! Dosar: ${res.data.numar_dosar}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const handleStatusChange = async (dosarId, newStatus) => {
    if (newStatus === "finalizat") {
      setStatusModal({ dosarId, status: newStatus });
      return;
    }
    try {
      await axios.patch(`${API}/consilier/dosare/${dosarId}/status`, { status: newStatus }, { headers });
      toast.success("Status actualizat");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const handleFinalizare = async () => {
    if (!sumaFinalizare || parseFloat(sumaFinalizare) <= 0) {
      toast.error("Introduceti suma despagubirii");
      return;
    }
    try {
      await axios.patch(`${API}/consilier/dosare/${statusModal.dosarId}/status`, { status: "finalizat", suma_despagubire: parseFloat(sumaFinalizare) }, { headers });
      toast.success("Dosar finalizat!");
      setStatusModal(null);
      setSumaFinalizare("");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
  };

  const openDosar = async (dosarId) => {
    try {
      const res = await axios.get(`${API}/consilier/dosare/${dosarId}`, { headers });
      setSelectedDosar(res.data);
    } catch { toast.error("Eroare"); }
  };

  const filteredDosare = useMemo(() => {
    if (dosarFilter === "active") return dosare.filter(d => ["nou", "in_verificare"].includes(d.status));
    if (dosarFilter === "aprobate") return dosare.filter(d => ["acceptat", "in_lucru"].includes(d.status));
    if (dosarFilter === "arhiva") return dosare.filter(d => ["finalizat", "respins"].includes(d.status));
    return dosare;
  }, [dosare, dosarFilter]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900" data-testid="consilier-title">Panou Consilier</h1>
        <button onClick={() => { logoutAdmin(); navigate("/affiliate/login"); }} className="flex items-center gap-2 text-gray-500 hover:text-red-500" data-testid="consilier-logout">
          <LogOut className="w-4 h-4" />Deconectare
        </button>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("leads")} className={`px-4 py-2 rounded-lg font-medium text-sm ${tab === "leads" ? "bg-emerald-500 text-white" : "bg-white text-gray-600 border"}`} data-testid="tab-leads">
            Leads Noi ({leads.length})
          </button>
          <button onClick={() => setTab("dosare")} className={`px-4 py-2 rounded-lg font-medium text-sm ${tab === "dosare" ? "bg-emerald-500 text-white" : "bg-white text-gray-600 border"}`} data-testid="tab-dosare">
            Dosarele Mele ({dosare.length})
          </button>
        </div>

        {tab === "leads" && (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="leads-table">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h2 className="font-semibold text-gray-900">Leads disponibile pentru preluare</h2>
            </div>
            {leads.length === 0 ? (
              <p className="p-6 text-gray-500 text-center">Nu exista leads noi.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left">Nume</th>
                      <th className="px-4 py-3 text-left">Telefon</th>
                      <th className="px-4 py-3 text-left">Oras</th>
                      <th className="px-4 py-3 text-left">Tip Dauna</th>
                      <th className="px-4 py-3 text-left">Data</th>
                      <th className="px-4 py-3 text-left">Cod Afiliat</th>
                      <th className="px-4 py-3 text-left">Doc.</th>
                      <th className="px-4 py-3 text-left">Actiune</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {leads.map(lead => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{lead.nume_prenume}</td>
                        <td className="px-4 py-3">{lead.telefon}</td>
                        <td className="px-4 py-3">{lead.oras || "-"}</td>
                        <td className="px-4 py-3">{lead.tip_dauna || "-"}</td>
                        <td className="px-4 py-3 text-gray-500">{lead.created_at?.slice(0, 10)}</td>
                        <td className="px-4 py-3">{lead.cod_afiliat || "-"}</td>
                        <td className="px-4 py-3">{lead.documente}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => handlePreluare(lead.id)} className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-medium hover:bg-emerald-600" data-testid={`preluare-${lead.id}`}>
                            Preia
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "dosare" && (
          <>
            <div className="flex gap-2 mb-4">
              {[
                { key: "active", label: "Active", icon: Clock },
                { key: "aprobate", label: "Aprobate", icon: CheckCircle },
                { key: "arhiva", label: "Arhiva", icon: Archive }
              ].map(f => (
                <button key={f.key} onClick={() => setDosarFilter(f.key)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium ${dosarFilter === f.key ? "bg-gray-900 text-white" : "bg-white text-gray-600 border hover:bg-gray-50"}`} data-testid={`filter-${f.key}`}>
                  <f.icon className="w-4 h-4" />{f.label}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="dosare-table">
              {filteredDosare.length === 0 ? (
                <p className="p-6 text-gray-500 text-center">Nu exista dosare in aceasta categorie.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left">Nr. Dosar</th>
                        <th className="px-4 py-3 text-left">Client</th>
                        <th className="px-4 py-3 text-left">Telefon</th>
                        <th className="px-4 py-3 text-left">Tip</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Actiuni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredDosare.map(d => (
                        <tr key={d.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono font-medium text-emerald-600">{d.numar_dosar}</td>
                          <td className="px-4 py-3 font-medium">{d.nume_prenume}</td>
                          <td className="px-4 py-3">{d.telefon}</td>
                          <td className="px-4 py-3">{d.tip_dauna || "-"}</td>
                          <td className="px-4 py-3">
                            <select value={d.status} onChange={(e) => handleStatusChange(d.id, e.target.value)} className={`px-2 py-1 rounded-lg text-xs font-medium border-0 cursor-pointer ${STATUS_COLORS[d.status] || ""}`} data-testid={`status-${d.id}`}>
                              {VALID_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => openDosar(d.id)} className="text-emerald-600 hover:text-emerald-800 text-xs font-medium" data-testid={`view-${d.id}`}>
                              Detalii
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {selectedDosar && (
          <ConsilierDosarModal
            dosar={selectedDosar}
            onClose={() => setSelectedDosar(null)}
            token={adminToken}
            onAddNote={async (dosarId, text) => {
              try {
                await axios.post(`${API}/consilier/dosare/${dosarId}/note`, { text }, { headers });
                toast.success("Nota adaugata");
                const res = await axios.get(`${API}/consilier/dosare/${dosarId}`, { headers });
                setSelectedDosar(res.data);
                fetchData();
              } catch { toast.error("Eroare"); }
            }}
            onAddVisibleNote={async (dosarId, text) => {
              try {
                await axios.post(`${API}/consilier/dosare/${dosarId}/note-vizibile`, { text }, { headers });
                toast.success("Nota vizibila adaugata");
                const res = await axios.get(`${API}/consilier/dosare/${dosarId}`, { headers });
                setSelectedDosar(res.data);
                fetchData();
              } catch { toast.error("Eroare"); }
            }}
            onUpdated={async () => {
              try {
                const res = await axios.get(`${API}/consilier/dosare/${selectedDosar.id}`, { headers });
                setSelectedDosar(res.data);
                fetchData();
              } catch { /* silent */ }
            }}
          />
        )}

        {statusModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6" data-testid="finalizare-modal">
              <h2 className="text-lg font-bold mb-4">Finalizare Dosar</h2>
              <p className="text-sm text-gray-600 mb-4">Introduceti suma despagubirii incasate:</p>
              <input type="number" value={sumaFinalizare} onChange={e => setSumaFinalizare(e.target.value)} placeholder="Suma in RON" className="w-full px-4 py-3 border rounded-lg mb-4" data-testid="suma-input" autoFocus />
              <div className="flex gap-3">
                <button onClick={() => { setStatusModal(null); setSumaFinalizare(""); }} className="flex-1 px-4 py-2 border rounded-lg text-gray-600">Anuleaza</button>
                <button onClick={handleFinalizare} className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium" data-testid="confirm-finalizare">Finalizeaza</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsilierDashboard;
